import React, { useState, useEffect } from "react";
import {
  Toolbar,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  FormControl,
  Select,
  Grid,
  List,
  IconButton,
  Divider,
  CircularProgress,
  Button,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { makeStyles } from "@mui/styles";
import { helpMenuList, profileMenuItem, sidebarMenuItems } from "utils/data";
import { useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { LOGOUT_START } from "Actions/AuthAction";
import { START_GET_PHARMACY_CONFIG } from "Actions/UserAction";
import { SET_HEADER_FILTER_PHARMACIES } from "Actions/PharmacyAction";
import {
  getLocalStorage,
  capitalizeFirstLetter,
  setLocalStorage,
} from "utils/generalUtils";
import { ReactComponent as MenuIcon } from "Assets/Icons/MenuIcon.svg";
import { ReactComponent as BranchIcon } from "Assets/Icons/BranchIcon.svg";
import HtmlTooltipMain from "Components/Common/HtmlTooltipMain";
import SideBarMenuItem from "Components/Common/SideBarCommanList";
import PrimaryIcon from "Components/Common/PrimaryIcon";
import { ReactComponent as Logout } from "Assets/Icons/Logout.svg";
import { ReactComponent as RepeatSmart } from "Assets/Logo/RepeatSmart.svg";
import { useIntersection } from "Hooks/customHooks";
import EditProfileDialog from "./EditProfileDialog";
import BillingDialog from "./BillingDialog";
import HelpIcon from "@mui/icons-material/Help";
import { ReactComponent as CloseIcon } from "../../Assets/Icons/CloseIcon.svg";
import clsx from "clsx";
import ContactUsDialog from "./ContactUsDialog";
import VideoDialogMain from "Components/HowTo/VideoDialogMain";
const drawerWidth = 240;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const useStyle = makeStyles((theme) => ({
  rsToolBarRoot: {
    background: "unset !important ",
    boxShadow: "none !important",
    backgroundColor: "#7bbd4b !important",
    boxShadow:
      "rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.1) 0px 0px 8px !important",
    "&>div": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      "&>button": {
        "&>svg": {
          "&>rect": {
            fill: "white",
          },
        },
      },
    },
  },
  rsHeaderRightSide: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    "& .MuiAvatar-root": {
      border: `2px solid ${theme.palette.white.main}`,
    },
    "&>button": {
      border: `1px solid${theme.palette.background.red} !important`,
      color: "#E6676B !important",
      background: theme.palette.white.main,
      "&:hover": {
        border: `none`,
        background: theme.palette.white.main,
      },
    },
  },
  rsFormControlRoot: {
    marginRight: "-1px !important",
    minWidth: "200px !important",
    "& .MuiOutlinedInput-notchedOutline": {
      border: 0,
    },
  },
  rsDivider: {
    background: `${theme.palette.background.grey} !important`,
    [theme.breakpoints.down("sm")]: {
      width: "87%",
      marginLeft: "55px !important",
    },
  },
  rsMainTitle: {
    color: "white !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.25rem !important",
    },
    [theme.breakpoints.down("400")]: {
      fontSize: "1.125rem !important",
    },
    [theme.breakpoints.down("336")]: {
      fontSize: "1rem !important",
    },
  },
  rsDrawerBtn: {
    [theme.breakpoints.down("400")]: {
      marginRight: "0px !important",
    },
  },
  rsDrawerListRoot: {
    "&>li": {
      "& .MuiListItemButton-root": {
        padding: "14px 16px",
        borderRadius: "4px",
      },
    },
    "& h2": {
      marginBottom: "30px",
      cursor: "pointer",
    },
  },
  rsBranchIcon: {
    marginRight: "39px",
    width: "30px",
    height: "30px",
    "&>path": {
      fill: "white",
    },
  },
  select: {
    textAlign: "center",
    [theme.breakpoints.down("336")]: {
      height: "35px !important",
    },
    [theme.breakpoints.down("321")]: {
      height: "32px !important",
    },
    "&>div": {
      paddingRight: "10px !important",
      paddingLeft: "10px !important",
      [theme.breakpoints.down("400")]: {
        paddingRight: "5px !important",
        paddingLeft: "5px !important",
        fontSize: "0.875rem",
      },
      [theme.breakpoints.down("336")]: {
        paddingRight: "5px !important",
        paddingLeft: "5px !important",
        fontSize: "0.813rem",
      },
      [theme.breakpoints.down("321")]: {
        paddingRight: "3px !important",
        paddingLeft: "3px !important",
        fontSize: "0.75rem",
      },
    },
  },
  rsFormControlRootPharmacyStaff: {
    background: theme.palette.white.main,
    boxShadow: "0px 3px 4px rgba(26, 102, 122, 0.1)",
    borderRadius: "5px",
    marginRight: "5px !important",
    minWidth: "102px !important",
    "& .MuiOutlinedInput-notchedOutline": {
      border: 0,
    },
  },
  rsWarningBox: {
    minHeight: "0px !important",
    textAlign: "end",
    justifyContent: "space-between",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    width: "100%",
    gap: "10px",
    marginBottom: "10px",
  },
  // rsBilling: {
  //   marginRight: "10px",
  //   width: "-webkit-fill-available",
  //   marginLeft: "10px",
  //   whiteSpace: "nowrap",
  //   "&>button": {
  //     marginTop: "10px",
  //     marginBottom: "10px",
  //     height: "30px",
  //   },
  //   [theme.breakpoints.down("420")]: {
  //     whiteSpace: "normal",
  //   },
  // },
  rsBilling: {
    marginRight: "5px",
    whiteSpace: "nowrap",
    display: "inline-flex",
    "&>button": {
      background: "white",
      color: "black",
      "&:hover": {
        background: "white",
        color: "black",
        opacity: "0.8",
      },
    },
  },
  rsBillingdaysleft: {
    marginRight: "20px",
    color: "red",
  },
  rsBillingButton: {
    "&>button": {
      background: "white",
      marginTop: "10px",
      marginBottom: "10px",
      height: "30px",
      whiteSpace: "nowrap",
      color: "black",
    },
  },
  rshelpIconMain: {
    marginRight: "0px",
    "&>button": {
      // background: "black",
    },
  },
  rsBoxColor: {
    position: "absolute",
    right: "10px",
    height: "fit-content",
    width: "450px",
    top: "110px",
    borderRadius: "10px",
    background: "white",
    boxShadow:
      "0px 8px 15px rgba(110, 117, 130, 0.15), 0px 1px 2px rgba(110, 117, 130, 0.3)",
    paddingBottom: "20px",
    zIndex: "15",
    [theme.breakpoints.down("500")]: {
      width: "350px",
    },
  },
  rsCloseIcon: {
    position: "absolute !important",
    right: "15px !important",
    top: "15px !important",
    [theme.breakpoints.down("500")]: {
      right: "5px !important",
      top: "5px !important",
    },
  },
  rsIconMain: {
    paddingTop: "25px",
    marginBottom: "10px",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("500")]: {
      paddingTop: "35px",
    },
  },
  rsRowMain: {
    background: "#fff",
    border: "solid 1px #dddddd",
    borderRadius: "4px",
    marginTop: "10px",
    padding: "12px 14px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    marginLeft: "10px",
    marginRight: "10px",
    "&>h5": {
      fontWeight: "500 !important",
      fontSize: "18px !important",
      color: "black",
    },
  },
  rsrighttitlecolor: {
    background: "rgba(234, 251, 227, 1)",
    borderRadius: "10px 10px 0px 0px",
    "&>h4": {
      color: "black",
      fontWeight: "600 !important",
      fontSize: "20px !important",
      paddingLeft: "20px",
      paddingBottom: "20px",
    },
  },
  rshelpButton: {
    // background: "black",
    color: "white !important",
  },
  btnWhite: {
    background: "white !important",
    color: "#7BC044 !important",
    marginRight: "15px !important",
    width: "132px",
    fontSize: "1rem !important",
    fontWeight: "400 !important",
    border: "2px solid #7BC044",
    transition: "transform 1s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "scale(1.05)",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px !important",
      width: "110px !important",
      lineHeight: "14px !important",
      padding: "0px 0px !important",
      marginRight: "0px !important",
    },
  },
  howToBtn: {
    background: "white !important",
    color: "#7BC044 !important",
    marginRight: "10px !important",
    fontSize: "1rem !important",
    fontWeight: "600 !important",
    border: "2px solid #7BC044",
    transition: "transform 1s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "scale(1.05)",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px !important",
      lineHeight: "14px !important",
      width: "110px !important",
    },
  },
}));

function HeaderMob({
  pharmacyConfigData,
  getPharmacyConfigAction,
  setHeaderFileterPharmaciesAction,
  loginUserDetail,
  logout,
  welcomeDialog,
  setWelcomeDialog,
  titleApp: propsTitleApp,
  setTitleApp: propsSetTitleApp,
  subTitleApp,
  setSubTitleApp,
}) {
  const classes = useStyle();
  const theme = useTheme();
  const location = useLocation();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [titleApp, setTitleApp] = useState("Dashboard");
  const [
    pharmacyFilterDropDownValueChange,
    setPharmacyFilterDropDownValueChange,
  ] = useState("all");
  const [openBranchInput, setOpenBranchInput] = useState(null);
  const [profileEditOpen, setProfileEditOpen] = useState(false);
  const [rightBarsetting, setRightBarsetting] = useState(false);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [linkvideo, setLinkvideo] = useState("");
  const [title, setTitle] = useState("");
  const openBranch = Boolean(openBranchInput);
  const [changeDropDownValue, setChangeDropDownValue] = useState("all");
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const navigate = useNavigate();
  /** Handle Click Open Branch List */
  const handleClickOpenBranchList = (event) => {
    setOpenBranchInput(event.currentTarget);
  };
  const [showProgress, setShowProgress] = useState(false);
  const [setTargetPharmacy, entryPharmacy] = useIntersection({
    threshold: 0.1,
  });
  const [paymentDialog, setPaymentDialog] = useState(false);
  /** Handle Close Branch List */
  const handleCloseBranchList = () => {
    setOpenBranchInput(null);
  };

  /** Handle Click Open Drawer */
  const handleDrawerOpen = () => {
    setOpenDrawer(true);
  };

  /** Handle Click Close Drawer */
  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  /** Handle Click Profile menu */
  const handleClickOpenProfileMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  /** Handle Click Menu Close */
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  /** Handle Click Logout */
  const handleClickLogout = (e, item) => {
    if (item?.key === "logout") {
      logout();
    }
  };

  /** Handle Change Pharmacy Header */
  const handleChangePharmacy = (event, value) => {
    setHeaderFileterPharmaciesAction({
      name: value?.props.children,
      id: value?.props?.value,
    });
    setLocalStorage("Pharmacies", value?.props?.value);
    setPharmacyFilterDropDownValueChange(event?.target?.value);
    setOpenBranchInput(null);
  };

  /** Set Header Title */
  const setHeaderTitle = () => {
    const activeModule = [...sidebarMenuItems].filter((item, index) => {
      return item.url === window.location.pathname;
    });
    const titleVal = activeModule?.[0]?.hasOwnProperty("getTitle")
      ? activeModule?.[0].getTitle()
      : activeModule?.[0]?.value;
    setTitleApp(titleVal);
    if (propsSetTitleApp) {
      propsSetTitleApp(titleVal);
    }
    if (setSubTitleApp) {
      const sub = activeModule?.[0]?.hasOwnProperty("getSubTitle")
        ? activeModule?.[0].getSubTitle()
        : activeModule?.[0]?.subTitle;
      setSubTitleApp(sub || "");
    }
  };

  /** Pharmacy header set in locat storage  */
  useEffect(() => {
    const selectedPharmcies = getLocalStorage("Pharmacies", true);
    if (selectedPharmcies != null && selectedPharmcies != "all") {
      setPharmacyFilterDropDownValueChange(selectedPharmcies);
      setChangeDropDownValue(selectedPharmcies);
    } else {
      setChangeDropDownValue("all");
      setPharmacyFilterDropDownValueChange("all");
    }
  }, []);

  /** Pharmacy api useffect */
  useEffect(() => {
    if (
      !pharmacyConfigData?.loading &&
      !Object.keys(pharmacyConfigData?.data)?.length
    ) {
      getPharmacyConfigAction({ offset: 0 });
    }
  }, [pharmacyConfigData]);

  /** get list on entry  */
  useEffect(() => {
    if (entryPharmacy.isIntersecting && pharmacyConfigData.loading === false) {
      let params = {
        offset: pharmacyConfigData?.data?.list?.length || 0,
      };
      if (
        pharmacyConfigData?.data?.list?.length !==
        pharmacyConfigData?.data?.total
      ) {
        getPharmacyConfigAction(params);
      } else {
        setShowProgress(false);
      }
    }
  }, [entryPharmacy]);

  /** Set Header Location Useffect */
  useEffect(() => {
    setHeaderTitle();
  }, [location]);

  const handleEditProfile = () => {
    setProfileEditOpen(true);
  };
  const [numberPart, setNumberPart] = useState(0);
  const [unitPart, setUnitPart] = useState("days");
  let tempDays = 10;
  let expireLimit = 31;
  // let numberPart = 0;
  // let unitPart = "days";
  useEffect(() => {
    if (loginUserDetail?.data?.data?.day_diff) {
      const trimmedString = loginUserDetail?.data?.data?.day_diff.trim();
      const parts = trimmedString?.split(" ");
      setNumberPart(parts[0]);
      setUnitPart(parts[1]);
    }
  }, [loginUserDetail?.data?.data]);
  const handleClose = () => {
    setRightBarsetting(false);
  };
  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        open={openDrawer}
        className={classes.rsToolBarRoot}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={classes.rsDrawerBtn}
            sx={{ mr: 2, ...(openDrawer && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>
          {!openDrawer && (
            <Typography variant="h3" medium className={classes.rsMainTitle}>
              {titleApp}
            </Typography>
          )}
          <Grid className={classes.rsHeaderRightSide}>
            {loginUserDetail?.data?.data?.is_new_user === false ? (
              <>
                <HtmlTooltipMain title="Pharmacie Branch">
                  <PrimaryIcon
                    className={classes.rsBranchIcon}
                    icon={BranchIcon}
                    onClick={handleClickOpenBranchList}
                  />
                </HtmlTooltipMain>
              </>
            ) : (
              <>
                {/* {loginUserDetail?.data?.data?.role === "user" && ( */}
                <FormControl
                  className={classes.rsFormControlRootPharmacyStaff}
                  InputLabelProps={{ shrink: true }}
                  disabled={loginUserDetail?.data?.data?.role === "user"}
                >
                  <Select
                    value={changeDropDownValue}
                    className={classes.select}
                    size="small"
                    IconComponent={null}
                  >
                    <MenuItem value={"all"}>
                      {loginUserDetail?.data?.data?.pharmacy_detail?.name
                        ? loginUserDetail?.data?.data?.pharmacy_detail?.name
                        : loginUserDetail?.data?.data?.name}
                    </MenuItem>
                  </Select>
                </FormControl>
                {/* )} */}
              </>
            )}

            <Avatar
              onClick={handleClickOpenProfileMenu}
              sx={{
                height: 38,
                width: 38,
              }}
            >
              {capitalizeFirstLetter(loginUserDetail?.data?.data?.name[0])}
            </Avatar>
          </Grid>
        </Toolbar>

        <Toolbar className={classes.rsWarningBox}>
          <Grid>
            {loginUserDetail?.data?.data?.is_applied_payment && (
              <>
                {/* {loginUserDetail?.data?.data?.is_new_user &&
                  loginUserDetail?.data?.data?.role != "super_admin" &&
                  (expireLimit < numberPart ? (
                    ""
                  ) : (
                    <Box className={classes.rsBilling}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setPaymentDialog(true);
                        }}
                      >
                        Billing ?
                      </Button>
                    </Box>
                  ))} */}
                {loginUserDetail?.data?.data?.is_trial &&
                  loginUserDetail?.data?.data?.is_new_user &&
                  loginUserDetail?.data?.data?.role != "nursing" &&
                  loginUserDetail?.data?.data?.role != "super_admin" &&
                  loginUserDetail?.data?.data?.role != "admin" &&
                  (expireLimit > numberPart ? (
                    <Box className={classes.rsBilling}>
                      <Button
                        variant="contained"
                        onClick={() => {
                          setPaymentDialog(true);
                        }}
                      >
                        Billing ?
                      </Button>
                    </Box>
                  ) : null)}
              </>
            )}
            <Box display={"flex !important"}>
              {loginUserDetail?.data?.data?.role !== "nursing" && (
                <Box>
                  <Button
                    onClick={() => {
                      navigate("/how-to");
                    }}
                    className={classes.howToBtn}
                    variant="contained"
                    style={{
                      width: "140px !important",
                    }}
                  >
                    How to Videos
                  </Button>
                </Box>
              )}
              {loginUserDetail?.data?.data?.role != "nursing" && (
                <Box>
                  <Button
                    onClick={() => {
                      window.open(
                        process.env.REACT_APP_BOOK_DEMO_URL,
                        "_blank",
                      );
                    }}
                    className={classes.btnWhite}
                    variant="contained"
                  >
                    Book Demo
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
          {loginUserDetail?.data?.data?.role !== "nursing" && (
            <Box className={classes.rshelpIconMain}>
              <IconButton
                className={clsx("help_effect", classes.rshelpButton)}
                variant="contained"
                onClick={() => {
                  setRightBarsetting(!rightBarsetting);
                }}
              >
                <PrimaryIcon
                  icon={HelpIcon}
                  color="white"
                  width="20px"
                  height="20px"
                />
              </IconButton>
            </Box>
          )}
        </Toolbar>

        {/* <Toolbar className={classes.rsWarningBox}>
          {loginUserDetail?.data?.data?.is_applied_payment && (
            <>
              {loginUserDetail?.data?.data?.is_trial &&
                loginUserDetail?.data?.data?.role != "super_admin" &&
                loginUserDetail?.data?.data?.is_new_user &&
                (unitPart === "days" ? (
                  <>
                    {numberPart > tempDays ? (
                      <Box
                        className={classes.rsBilling}
                      >{`Trial: ${loginUserDetail?.data?.data?.day_diff} left`}</Box>
                    ) : (
                      <Box className={classes.rsBillingdaysleft}>
                        {`Trial:  ${loginUserDetail?.data?.data?.day_diff} left MUST SIGNUP NOW`}
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Box className={classes.rsBillingdaysleft}>
                      {`Trial:  ${loginUserDetail?.data?.data?.day_diff} left MUST SIGNUP NOW`}
                    </Box>
                  </>
                ))}

              {!loginUserDetail?.data?.data?.is_trial &&
                loginUserDetail?.data?.data?.is_new_user &&
                loginUserDetail?.data?.data?.role != "super_admin" &&
                loginUserDetail?.data?.data?.role != "admin" &&
                !loginUserDetail?.data?.data?.is_expired && (
                  <Box className={classes.rsBilling}>
                    {expireLimit < numberPart
                      ? ""
                      : //  `${loginUserDetail?.data?.data?.day_diff} remaining `
                        `${loginUserDetail?.data?.data?.day_diff}  until subscription expires `}
                  </Box>
                )}
              {!loginUserDetail?.data?.data?.is_trial &&
                loginUserDetail?.data?.data?.role != "super_admin" &&
                loginUserDetail?.data?.data?.is_expired && (
                  <Box className={classes.rsBillingdaysleft}>
                    Subscription expired!
                  </Box>
                )}

              {loginUserDetail?.data?.data?.is_new_user &&
                loginUserDetail?.data?.data?.role != "super_admin" &&
                (expireLimit < numberPart ? (
                  ""
                ) : (
                  <Box className={classes.rsBilling}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setPaymentDialog(true);
                      }}
                    >
                      Billing ?
                    </Button>
                  </Box>
                ))}
            </>
          )}
          <Box className={classes.rshelpIconMain}>
            <IconButton
              className={clsx("help_effect", classes.rshelpButton)}
              variant="contained"
              onClick={() => {
                setRightBarsetting(!rightBarsetting);
              }}
            >
              <PrimaryIcon
                icon={HelpIcon}
                color="white"
                width="20px"
                height="20px"
              />
            </IconButton>
          </Box>
        </Toolbar> */}
        <Divider className={classes.rsDivider} />
      </AppBar>
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            borderRadius: "10px",
            width: "10%",
            width: "170px",
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem>
          <Avatar className={classes.rsProfileIcon}>
            {capitalizeFirstLetter(loginUserDetail?.data?.data?.name[0])}
          </Avatar>
          <Box
            style={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              width: "113px",
            }}
            onClick={() => {
              handleEditProfile();
            }}
          >
            {loginUserDetail?.data?.data?.name.split(" ")[0]}
            {loginUserDetail?.data?.data?.role === "pharmacist_staff" && (
              <Typography variant="h6" medium className={classes.rsProfileRole}>
                {capitalizeFirstLetter("pharmacist Staff")}
              </Typography>
            )}
          </Box>
        </MenuItem>
        <Divider sx={{ background: "#F7F7F8 !important" }} />
        {profileMenuItem?.map((item) => {
          return (
            <MenuItem onClick={(e) => handleClickLogout(e, item)}>
              <Logout
                style={{
                  height: 20,
                  width: 20,
                  marginRight: "15px",
                }}
              />
              {item.value}
            </MenuItem>
          );
        })}
      </Menu>
      <Menu
        anchorEl={openBranchInput}
        open={openBranch}
        onClose={handleCloseBranchList}
      >
        <FormControl
          className={classes.rsFormControlRoot}
          InputLabelProps={{ shrink: true }}
        >
          <Select
            value={pharmacyFilterDropDownValueChange}
            onChange={(e, value) => handleChangePharmacy(e, value)}
            disabled={
              ["/surgery", "/staff", "/activity", "/pharmacies"].indexOf(
                window.location.pathname,
              ) !== -1 || pharmacyConfigData?.loading
            }
            size="small"
          >
            <MenuItem
              value={"all"}
              onClick={() => {
                localStorage.removeItem("pharmacyEmail");
                // localStorage.removeItem("pharmacyEmail");
                // setLocalStorage("pharmacyEmail", null);
              }}
            >
              All Branches
            </MenuItem>
            {pharmacyConfigData?.data?.list?.map((item, index) => (
              <MenuItem
                key={index}
                value={item?.id}
                onClick={() => {
                  setLocalStorage(
                    "pharmacyEmail",
                    item?.cc_email ? item?.cc_email : null,
                  );
                }}
              >
                {item?.name}
              </MenuItem>
            ))}
            {pharmacyConfigData?.data?.list?.length !==
              pharmacyConfigData?.data?.total && (
              <MenuItem disabled ref={setTargetPharmacy}>
                <CircularProgress size={20} />
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Menu>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
        open={openDrawer}
      >
        <DrawerHeader>
          {openDrawer && (
            <Typography variant="h3" medium className={classes.rsMainTitle}>
              {titleApp}
            </Typography>
          )}
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <List className={classes.rsDrawerListRoot}>
          <Typography
            style={{
              textAlign: openDrawer ? "left" : "center",
              marginLeft: openDrawer ? "10px" : "0px",
            }}
            variant="h2"
            semiBold
            color="primary"
          >
            {!openDrawer ? (
              "R"
            ) : (
              <PrimaryIcon
                style={{
                  width: "200px",
                  height: "37px",
                  marginLeft: "10px",
                }}
                icon={RepeatSmart}
                cursor="pointer"
              />
            )}
          </Typography>
          <SideBarMenuItem
            loginUserDetail={loginUserDetail}
            open={openDrawer}
            welcomeDialog={welcomeDialog}
            setWelcomeDialog={setWelcomeDialog}
          />
        </List>
      </Drawer>
      <Main open={openDrawer} style={{ marginTop: "0px" }}>
        <DrawerHeader />
      </Main>
      {rightBarsetting && (
        <Box className={classes.rsBoxColor}>
          <Box className={classes.rsrighttitlecolor}>
            <Box className={classes.rsIconMain}>
              <PrimaryIcon
                style={{
                  width: "90%",
                  height: "65px",
                  marginLeft: "10px",
                  // marginBottom: "-22px",
                }}
                icon={RepeatSmart}
              />
            </Box>
            <Typography variant="h4">Resource Center</Typography>
            <IconButton
              onClick={() => handleClose()}
              className={classes.rsCloseIcon}
            >
              {" "}
              <PrimaryIcon icon={CloseIcon} />{" "}
            </IconButton>
          </Box>
          {helpMenuList.map((item) => {
            return loginUserDetail?.data?.data?.role !== "admin" &&
              loginUserDetail?.data?.data?.is_applied_payment &&
              item?.id !== "billing" ? (
              <Box
                className={classes.rsRowMain}
                onClick={() => {
                  if (item?.id === "contact") {
                    setOpenContactDialog(true);
                  }
                  if (item?.id === "billing") {
                    setPaymentDialog(true);
                  }
                  if (item?.navigation) {
                    navigate(item?.navigation);
                    // setLinkvideo(item?.link);
                    // setTitle(item?.title);
                    // setVideoDialogOpen(true);
                  }
                }}
              >
                <Typography variant="h5">{item?.title}</Typography>
                <PrimaryIcon icon={ChevronRightIcon} />
              </Box>
            ) : item?.id === "billing" &&
              loginUserDetail?.data?.data?.is_new_user === false ? (
              ""
            ) : (
              <Box
                className={classes.rsRowMain}
                onClick={() => {
                  if (item?.id === "contact") {
                    setOpenContactDialog(true);
                  }
                  if (item?.id === "billing") {
                    setPaymentDialog(true);
                  }
                  if (item?.navigation) {
                    navigate(item?.navigation);
                    // setLinkvideo(item?.link);
                    // setTitle(item?.title);
                    // setVideoDialogOpen(true);
                  }
                }}
              >
                <Typography variant="h5">{item?.title}</Typography>
                <PrimaryIcon icon={ChevronRightIcon} />
              </Box>
            );
          })}
        </Box>
      )}
      <EditProfileDialog open={profileEditOpen} setOpen={setProfileEditOpen} />
      {paymentDialog && (
        <BillingDialog open={paymentDialog} setOpen={setPaymentDialog} />
      )}
      <ContactUsDialog
        open={openContactDialog}
        setOpen={setOpenContactDialog}
      />
      <VideoDialogMain
        open={videoDialogOpen}
        setOpen={setVideoDialogOpen}
        link={linkvideo}
        title={title}
      />
    </Box>
  );
}

const mapStateToProps = (state) => ({
  loginUserDetail: state.user.loginUserDetail,
  pharmacyConfigData: state.user.pharmacyConfigData,
  noOfAlertData: state.home.noOfAlertData,
  repeatOrderList: state.order.repeatOrderList,
  homeList: state.home.homeList,
});
const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch({ type: LOGOUT_START }),
  getPharmacyConfigAction: (payload) =>
    dispatch({ type: START_GET_PHARMACY_CONFIG, payload }),
  setHeaderFileterPharmaciesAction: (payload) =>
    dispatch({ type: SET_HEADER_FILTER_PHARMACIES, payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(HeaderMob);
